/**
 * Utilitário de sanitização de HTML
 * Protege contra XSS e injeção de código malicioso
 * 
 * Versão: 1.1.0
 * - v1.1.0: Adicionada função stripHtml para remoção completa de tags HTML
 */
import DOMPurify from 'dompurify';

// Tags HTML permitidas
const ALLOWED_TAGS = [
  'p', 'br', 'span', 'div',
  'strong', 'b', 'em', 'i', 'u', 's', 'strike',
  'ul', 'ol', 'li',
  'a', 'img',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'blockquote', 'pre', 'code',
  'hr'
];

// Atributos permitidos
const ALLOWED_ATTR = [
  'href', 'src', 'alt', 'title', 'target', 'rel',
  'class', 'style'
];

/**
 * Sanitiza conteúdo HTML para exibição segura
 * Remove scripts, eventos JS e outros vetores de XSS
 * 
 * @param html - Conteúdo HTML a ser sanitizado
 * @returns HTML sanitizado e seguro para exibição
 */
export function sanitizeHtml(html: string | null | undefined): string {
  if (!html) return '';
  
  // Aplicar sanitização com configuração restrita
  const sanitized = DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
  });
  
  return sanitized;
}

/**
 * Verifica se o HTML contém conteúdo potencialmente malicioso
 * Útil para validação antes de enviar ao backend
 * 
 * @param html - Conteúdo HTML a ser verificado
 * @returns true se detectar conteúdo suspeito
 */
export function hasUnsafeContent(html: string | null | undefined): boolean {
  if (!html) return false;
  
  // Padrões de conteúdo malicioso
  const unsafePatterns = [
    /<script/i,
    /javascript:/i,
    /onerror\s*=/i,
    /onload\s*=/i,
    /onclick\s*=/i,
    /onmouseover\s*=/i,
    /onfocus\s*=/i,
    /onblur\s*=/i,
    /eval\s*\(/i,
    /expression\s*\(/i,
    /data:/i,
    /vbscript:/i,
  ];
  
  return unsafePatterns.some(pattern => pattern.test(html));
}

/**
 * Remove todas as tags HTML e retorna texto puro
 * Útil para exibição resumida (cards, listagens, previews)
 * 
 * @param html - Conteúdo HTML a ser convertido em texto puro
 * @returns Texto sem tags HTML, entidades resolvidas e espaços normalizados
 */
export function stripHtml(html: string | null | undefined): string {
  if (!html) return '';
  
  return html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Sanitiza e valida HTML antes de salvar
 * Combina sanitização com validação de segurança
 * 
 * @param html - Conteúdo HTML a ser processado
 * @returns Objeto com HTML sanitizado e status de segurança
 */
export function processHtmlForSave(html: string | null | undefined): {
  sanitized: string;
  isValid: boolean;
  originalHadUnsafeContent: boolean;
} {
  const originalHadUnsafeContent = hasUnsafeContent(html);
  const sanitized = sanitizeHtml(html);
  
  return {
    sanitized,
    isValid: !hasUnsafeContent(sanitized),
    originalHadUnsafeContent
  };
}
